import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Search, XCircle } from 'lucide-react'
import { apiService } from '@/services/api'
import { formatDate } from '@/shared/lib/dateUtils'
import { EsqueletoTabla } from '@/shared/components/Esqueleto'

interface HiredCandidate {
  id: string
  fullName: string
  email: string
  phone: string
  status: string
  applicationDate: string
}

interface CompletedJobRequest {
  id: number
  vacante: string
  area: string
  fechaPublicacion: string
  candidatosQueAplicaron: number
  estado: 'Contratación' | 'Pausada' | 'Cancelada'
  candidatoContratado: HiredCandidate | null
  fechaCierre: string
}

interface JobRequestsHistoryProps {
  /**
   * Cuando vive dentro del panel de reclutamiento no pinta su propio
   * encabezado: el título, la descripción y el buscador ya están arriba, y
   * repetirlos deja la pantalla con dos de cada uno.
   */
  embedded?: boolean
  /** Término de búsqueda del panel. Solo aplica embebido. */
  busqueda?: string
}

export const JobRequestsHistory = ({ embedded = false, busqueda }: JobRequestsHistoryProps = {}) => {
  const navigate = useNavigate()
  const [jobRequests, setJobRequests] = useState<CompletedJobRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTermPropio, setSearchTerm] = useState('')
  const searchTerm = embedded ? (busqueda ?? '') : searchTermPropio

  useEffect(() => {
    loadCompletedJobRequests()
  }, [])

  const loadCompletedJobRequests = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await apiService.getCompletedJobRequests()
      setJobRequests(data)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al cargar el historial de vacantes')
    } finally {
      setIsLoading(false)
    }
  }

  // Filtrar por búsqueda
  const filteredJobRequests = jobRequests.filter(jr => {
    const searchLower = searchTerm.toLowerCase()
    const vacanteMatch = jr.vacante?.toLowerCase().includes(searchLower) || false
    const areaMatch = jr.area?.toLowerCase().includes(searchLower) || false
    const candidatoMatch = jr.candidatoContratado?.fullName?.toLowerCase().includes(searchLower) || false
    
    return vacanteMatch || areaMatch || candidatoMatch
  })

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <EsqueletoTabla filas={7} columnas={6} />
      </div>
    )
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50 my-8'}>
      {/* Encabezado propio: solo en pantalla completa. */}
      {!embedded && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto">
            {/* Back button and title */}
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => navigate(-1)}
                className={estiloDeBoton('secundario')}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Volver</span>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <TituloDePantalla>Historial de vacantes</TituloDePantalla>
                <p className="text-sm text-gray-600 mt-1">Panel de candidatos / Historial de vacantes</p>
              </div>

              {/* Search */}
              <div className="relative w-full sm:w-96">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar en Historial de vacantes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {/* Embebido en el panel ocupa todo el ancho, como las otras pestañas;
          suelto en su propia ruta conserva el tope y se centra. */}
      <div className={embedded ? 'my-4' : 'max-w-7xl mx-auto my-8'}>
        <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                    Vacante
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                    Fecha de publicación
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                    Candidatos que aplicaron
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                    Estado
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                    Candidato contratado
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                    Fecha de cierre
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {filteredJobRequests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <p className="text-sm text-gray-400">No hay vacantes en el historial</p>
                    </td>
                  </tr>
                ) : (
                  filteredJobRequests.map((jr) => (
                    <tr key={jr.id} className="hover:bg-[#F4F7FB] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{jr.vacante}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{formatDate(jr.fechaPublicacion)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-medium">{jr.candidatosQueAplicaron}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{jr.estado}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {jr.candidatoContratado?.fullName ?? 'n/a'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{formatDate(jr.fechaCierre)}</div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer info */}
        {filteredJobRequests.length > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            Mostrando {filteredJobRequests.length} de {jobRequests.length} vacantes
          </div>
        )}
      </div>
    </div>
  )
}
